<template>
  <div
    v-if="yandleAgentEnabled"
    class="yandle-agent-fab-root"
    :class="{ 'yandle-agent-fab-root--inline': isInlineVariant }"
  >
    <transition name="yandle-agent-panel">
      <div
        v-if="panelOpen"
        class="yandle-agent-panel"
        :class="{
          'yandle-agent-panel--chat': isChatPanelActive,
          'yandle-agent-panel--voice': isVoicePanelActive,
          'yandle-agent-panel--anchored': isInlineVariant,
        }"
        role="dialog"
        aria-label="Yandle AI assistant"
      >
        <div class="yandle-agent-panel__bar">
          <span class="yandle-agent-panel__label">
            {{
              isChatPanelActive
                ? 'AI Chat'
                : isVoicePanelActive
                  ? 'AI Call'
                  : 'Yandle Agent'
            }}
          </span>
          <span
            v-if="
              isAgentPanelActive &&
              agentSession?.subtitle &&
              !transcriptLines.length
            "
            class="yandle-agent-panel__status"
          >
            {{ agentSession.subtitle }}
          </span>
          <v-spacer />
          <v-btn
            icon
            variant="text"
            size="small"
            density="comfortable"
            aria-label="Close assistant panel"
            @click="closePanel"
          >
            <v-icon size="20">mdi-close</v-icon>
          </v-btn>
        </div>

        <div
          class="yandle-agent-panel__body"
          :class="{
            'yandle-agent-panel__body--chat': isChatPanelActive,
            'yandle-agent-panel__body--voice': isVoicePanelActive,
          }"
        >
          <div
            class="yandle-agent-panel__content"
            :class="{
              'yandle-agent-panel__content--chat': isChatPanelActive,
              'yandle-agent-panel__content--voice': isVoicePanelActive,
            }"
          >
            <div class="yandle-agent-modes">
              <template v-if="!agentSessionActive">
                <v-chip
                  size="small"
                  color="primary"
                  variant="flat"
                  class="mode-chip mode-chip--action"
                  role="button"
                  tabindex="0"
                  :disabled="voiceLoading || !hasTenantId"
                  @click="onVoiceCallClick"
                  @keydown.enter.prevent="onVoiceCallClick"
                  @keydown.space.prevent="onVoiceCallClick"
                >
                  <v-icon v-if="!voiceLoading" start size="16"
                    >mdi-phone-in-talk</v-icon
                  >
                  <v-progress-circular
                    v-else
                    indeterminate
                    size="16"
                    width="2"
                    class="mode-chip__spinner"
                  />
                  {{ voiceLoading ? 'Starting…' : 'Talk to agent' }}
                </v-chip>
                <v-chip
                  size="small"
                  color="secondary"
                  variant="tonal"
                  class="mode-chip mode-chip--action"
                  role="button"
                  tabindex="0"
                  :disabled="chatLoading || !hasTenantId"
                  @click="onChatStartClick"
                  @keydown.enter.prevent="onChatStartClick"
                  @keydown.space.prevent="onChatStartClick"
                >
                  <v-icon v-if="!chatLoading" start size="16"
                    >mdi-message-text-outline</v-icon
                  >
                  <v-progress-circular
                    v-else
                    indeterminate
                    size="16"
                    width="2"
                    class="mode-chip__spinner"
                  />
                  {{ chatLoading ? 'Connecting…' : 'Chat with agent' }}
                </v-chip>
              </template>
              <v-chip
                v-else
                size="small"
                color="error"
                variant="tonal"
                class="mode-chip mode-chip--action"
                role="button"
                tabindex="0"
                @click="onEndAgentSession"
                @keydown.enter.prevent="onEndAgentSession"
                @keydown.space.prevent="onEndAgentSession"
              >
                <v-icon start size="16">{{
                  agentMode === 'chat' ? 'mdi-chat-remove' : 'mdi-phone-hangup'
                }}</v-icon>
                {{ agentMode === 'chat' ? 'End chat' : 'Stop call' }}
              </v-chip>
            </div>

            <div v-if="!hasTenantId" class="chat-empty-hint">
              <v-icon size="32" color="warning" class="mb-2"
                >mdi-alert-circle-outline</v-icon
              >
              <p class="chat-empty-hint__title">Organisation not ready</p>
              <p class="chat-empty-hint__subtitle">
                Your workspace needs an internal organisation id before the AI
                agent can connect.
              </p>
            </div>

            <div v-else class="chat-thread">
              <div
                v-if="transcriptLines.length"
                ref="transcriptScrollEl"
                class="chat-messages"
                role="log"
                aria-live="polite"
                aria-relevant="additions"
              >
                <div
                  v-for="(line, index) in transcriptLines"
                  :key="`${line.role}-${line.timestamp || index}-${index}`"
                  class="chat-row"
                  :class="
                    isUserTranscriptRole(line.role)
                      ? 'chat-row--user'
                      : 'chat-row--agent'
                  "
                >
                  <div class="chat-row__avatar" aria-hidden="true">
                    <v-icon
                      size="18"
                      :icon="transcriptLineIcon(line.role)"
                    />
                  </div>
                  <div class="chat-row__content">
                    <div class="chat-row__meta">
                      <span class="chat-row__sender">{{
                        transcriptSenderLabel(line)
                      }}</span>
                      <time
                        class="chat-row__time"
                        :datetime="line.timestamp || undefined"
                      >
                        {{ transcriptTimeLabel(line) }}
                      </time>
                    </div>
                    <p class="chat-row__bubble">{{ line.text }}</p>
                  </div>
                </div>
                <div
                  v-if="isChatPanelActive && agentTyping"
                  class="chat-row chat-row--agent chat-row--typing"
                  aria-live="polite"
                >
                  <div class="chat-row__avatar" aria-hidden="true">
                    <v-icon size="18" icon="mdi-robot-outline" />
                  </div>
                  <div class="chat-row__content">
                    <div class="chat-row__meta">
                      <span class="chat-row__sender">{{ chatAgentName }}</span>
                      <span class="chat-row__time">Typing…</span>
                    </div>
                    <div
                      class="chat-typing-bubble"
                      role="status"
                      aria-label="Assistant is typing"
                    >
                      <span />
                      <span />
                      <span />
                    </div>
                  </div>
                </div>
                <div
                  v-if="isVoicePanelActive && agentSpeaking"
                  class="chat-row chat-row--agent chat-row--typing"
                  aria-live="polite"
                >
                  <div class="chat-row__avatar" aria-hidden="true">
                    <v-icon size="18" icon="mdi-robot-outline" />
                  </div>
                  <div class="chat-row__content">
                    <div class="chat-row__meta">
                      <span class="chat-row__sender">{{ chatAgentName }}</span>
                      <span class="chat-row__time">Speaking…</span>
                    </div>
                    <div
                      class="chat-typing-bubble chat-typing-bubble--voice"
                      role="status"
                      aria-label="Agent is speaking"
                    >
                      <span />
                      <span />
                      <span />
                    </div>
                  </div>
                </div>
              </div>
              <div
                v-else-if="isChatPanelActive && (chatLoading || !chatCanSend)"
                class="chat-empty-hint"
              >
                <v-progress-circular
                  v-if="chatLoading"
                  indeterminate
                  size="28"
                  width="2"
                  color="primary"
                  class="mb-3"
                />
                <v-icon
                  v-else
                  size="32"
                  color="primary"
                  class="mb-2"
                  icon="mdi-message-text-outline"
                />
                <p class="chat-empty-hint__title">
                  {{ chatLoading ? 'Connecting…' : 'Almost ready…' }}
                </p>
              </div>
              <div v-else-if="isChatPanelActive" class="chat-empty-hint">
                <v-icon
                  size="32"
                  color="primary"
                  class="mb-2"
                  icon="mdi-message-text-outline"
                />
                <p class="chat-empty-hint__title">
                  Message {{ chatAgentName }}
                </p>
                <p class="chat-empty-hint__subtitle">
                  Hours, services, getting started…
                </p>
              </div>
              <div
                v-else-if="isVoicePanelActive && voicePanelLoading"
                class="chat-empty-hint"
              >
                <v-progress-circular
                  indeterminate
                  size="28"
                  width="2"
                  color="primary"
                  class="mb-3"
                />
                <p class="chat-empty-hint__title">
                  {{ voiceLoading ? 'Starting call…' : 'Connecting…' }}
                </p>
              </div>
              <div v-else-if="isVoicePanelActive" class="chat-empty-hint">
                <v-icon
                  size="32"
                  color="primary"
                  class="mb-2"
                  icon="mdi-microphone"
                />
                <p class="chat-empty-hint__title">
                  Speak to {{ chatAgentName }}
                </p>
                <p class="chat-empty-hint__subtitle">
                  Live transcript appears here as you talk.
                </p>
              </div>
              <div
                v-else-if="!agentSessionActive"
                class="chat-empty-hint"
              >
                <v-icon
                  size="32"
                  color="primary"
                  class="mb-2"
                  icon="mdi-robot-outline"
                />
                <p class="chat-empty-hint__title">Try the AI agent</p>
                <p class="chat-empty-hint__subtitle">
                  Start a voice call or chat with your organisation assistant.
                </p>
              </div>
            </div>

            <div
              v-if="isVoicePanelActive"
              class="voice-call-bar"
              role="status"
              aria-live="polite"
            >
              <v-icon
                size="20"
                :icon="agentSessionIcon"
                :color="agentSessionIconColor"
              />
              <div class="voice-call-bar__text">
                <span class="voice-call-bar__title">{{
                  agentSession?.title || 'On call'
                }}</span>
                <span
                  v-if="agentSession?.subtitle"
                  class="voice-call-bar__subtitle"
                >
                  {{ agentSession.subtitle }}
                </span>
              </div>
            </div>

            <form
              v-if="agentMode === 'chat' && agentSessionActive"
              class="chat-compose"
              @submit.prevent="onChatSend"
            >
              <div class="chat-compose__field">
                <v-text-field
                  ref="chatInput"
                  v-model="chatDraft"
                  density="compact"
                  variant="outlined"
                  hide-details
                  placeholder="Type your message…"
                  class="chat-compose__input"
                  :disabled="!chatCanSend"
                  autocomplete="off"
                  @keydown.enter.exact.prevent="onChatSend"
                />
                <p class="chat-compose__hint">Enter to send</p>
              </div>
              <v-btn
                type="submit"
                color="primary"
                variant="flat"
                icon="mdi-send"
                class="chat-compose__send"
                :disabled="!chatCanSend || !chatDraft.trim()"
                aria-label="Send message"
              />
            </form>
          </div>
        </div>
      </div>
    </transition>

    <v-btn
      class="yandle-agent-fab"
      :color="fabColor"
      :size="isInlineVariant ? 'small' : 'large'"
      elevation="8"
      :aria-expanded="panelOpen ? 'true' : 'false'"
      :aria-label="triggerLabel || 'Open Yandle AI assistant'"
      @click="togglePanel"
    >
      <v-icon :size="isInlineVariant ? 20 : 28">{{
        panelOpen ? 'mdi-close' : 'mdi-robot-outline'
      }}</v-icon>
      <span v-if="isInlineVariant" class="yandle-agent-fab__label">
        {{ triggerLabel }}
      </span>
    </v-btn>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { formatChatTimestamp } from '@/utils/formatChatTimestamp.js'
import { isYandleAgentHostEnabled } from '@/utils/yandleAgentHost.js'
import {
  startYandleVoiceCall,
  stopYandleVoiceCall,
} from '@/utils/yandleVoiceWidget.js'
import {
  sendYandleChatMessage,
  startYandleChatSession,
  stopYandleChatSession,
} from '@/utils/yandleChatWidget.js'

export default {
  name: 'YandleAgentFab',
  props: {
    variant: {
      type: String,
      default: 'fab',
    },
    triggerLabel: {
      type: String,
      default: 'Yandle Agent',
    },
    orgName: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      panelOpen: false,
      agentMode: null,
      agentSession: null,
      transcriptLines: [],
      chatDraft: '',
      agentTyping: false,
      voiceLoading: false,
      chatLoading: false,
    }
  },
  computed: {
    ...mapState(['organization', 'landingOrganization']),
    isInlineVariant() {
      return this.variant === 'inline'
    },
    yandleAgentEnabled() {
      return isYandleAgentHostEnabled()
    },
    agentTenantId() {
      const sessionId = String(
        this.organization?.internal_org_id ?? '',
      ).trim()
      if (sessionId) return sessionId
      return String(this.landingOrganization?.internal_org_id ?? '').trim()
    },
    hasTenantId() {
      return Boolean(this.agentTenantId)
    },
    displayOrgName() {
      const fromProp = String(this.orgName ?? '').trim()
      if (fromProp) return fromProp
      return (
        this.organization?.org_name?.trim() ||
        this.landingOrganization?.org_name?.trim() ||
        ''
      )
    },
    fabColor() {
      return this.agentSessionActive ? 'error' : 'primary'
    },
    voiceSessionActive() {
      return (
        this.agentMode === 'voice' &&
        this.agentSessionActive &&
        !['call_ended', 'error'].includes(this.agentSession?.phase)
      )
    },
    chatSessionActive() {
      return (
        this.agentMode === 'chat' &&
        this.agentSessionActive &&
        !['chat_ended', 'error'].includes(this.agentSession?.phase)
      )
    },
    agentSessionActive() {
      const s = this.agentSession
      if (!s) return false
      const endedPhases =
        this.agentMode === 'chat'
          ? ['chat_ended', 'error']
          : ['call_ended', 'error']
      return !endedPhases.includes(s.phase)
    },
    isChatPanelActive() {
      return this.agentMode === 'chat' && this.agentSessionActive
    },
    isVoicePanelActive() {
      return this.agentMode === 'voice' && this.agentSessionActive
    },
    isAgentPanelActive() {
      return this.isChatPanelActive || this.isVoicePanelActive
    },
    agentSpeaking() {
      return (
        this.isVoicePanelActive &&
        this.agentSession?.phase === 'agent_speaking'
      )
    },
    voicePanelLoading() {
      if (!this.isVoicePanelActive) return false
      if (this.voiceLoading) return true
      const phase = this.agentSession?.phase
      return phase === 'connecting' || phase === 'connected'
    },
    chatCanSend() {
      if (this.agentMode !== 'chat' || !this.chatSessionActive) return false
      const phase = this.agentSession?.phase
      return phase === 'connected' || phase === 'session_update'
    },
    chatAgentName() {
      const name =
        this.agentSession?.orgName || this.displayOrgName || 'Yandle'
      return `${name} Assistant`
    },
    agentSessionIcon() {
      const phase = this.agentSession?.phase
      if (this.agentMode === 'chat') {
        const chatMap = {
          connecting: 'mdi-sync',
          connected: 'mdi-chat-processing',
          session_update: 'mdi-information-outline',
          chat_ended: 'mdi-chat-remove',
          error: 'mdi-alert-circle-outline',
        }
        return chatMap[phase] || 'mdi-message-text-outline'
      }
      const voiceMap = {
        connecting: 'mdi-sync',
        connected: 'mdi-wifi',
        agent_ready: 'mdi-headset',
        listening: 'mdi-microphone',
        agent_speaking: 'mdi-volume-high',
        reconnecting: 'mdi-sync',
        session_update: 'mdi-information-outline',
        call_ended: 'mdi-phone-off',
        error: 'mdi-alert-circle-outline',
      }
      return voiceMap[phase] || 'mdi-phone-in-talk'
    },
    agentSessionIconColor() {
      const phase = this.agentSession?.phase
      if (phase === 'error') return 'error'
      if (phase === 'call_ended' || phase === 'chat_ended') return undefined
      if (phase === 'reconnecting') return 'warning'
      if (phase === 'agent_speaking') return 'primary'
      return 'primary'
    },
  },
  watch: {
    transcriptLines() {
      if (this.isAgentPanelActive) {
        this.scrollTranscriptToBottom()
      }
    },
    'agentSession.phase'(phase) {
      if (this.isChatPanelActive && phase === 'connected') {
        this.scrollTranscriptToBottom()
        this.focusChatInput()
      }
      if (this.isVoicePanelActive) {
        this.scrollTranscriptToBottom()
      }
    },
    agentTyping() {
      if (this.isChatPanelActive) {
        this.scrollTranscriptToBottom()
      }
    },
    agentSpeaking() {
      if (this.isVoicePanelActive) {
        this.scrollTranscriptToBottom()
      }
    },
  },
  beforeUnmount() {
    stopYandleChatSession({ silent: true })
    stopYandleVoiceCall({ silent: true })
  },
  methods: {
    togglePanel() {
      this.panelOpen = !this.panelOpen
    },
    closePanel() {
      this.panelOpen = false
    },
    isUserTranscriptRole(role) {
      return role === 'caller' || role === 'user'
    },
    resetAgentUi() {
      this.transcriptLines = []
      this.chatDraft = ''
      this.agentTyping = false
      this.agentSession = null
      this.agentMode = null
    },
    onAgentSessionUpdate(state) {
      this.agentSession = { ...state }
    },
    onTranscriptUpdate(line) {
      if (this.agentMode === 'chat' && line.role === 'user') {
        const pendingIndex = this.transcriptLines.findLastIndex(
          (entry) =>
            entry.pending &&
            entry.role === 'user' &&
            entry.text === line.text,
        )
        if (pendingIndex >= 0) {
          const next = [...this.transcriptLines]
          next[pendingIndex] = { ...line, pending: false }
          this.transcriptLines = next
          this.agentTyping = false
          this.scrollTranscriptToBottom()
          return
        }
      }
      if (line.role === 'agent') {
        this.agentTyping = false
      }
      this.transcriptLines = [...this.transcriptLines, { ...line }]
      if (this.isAgentPanelActive) {
        this.scrollTranscriptToBottom()
      }
    },
    onEndAgentSession() {
      if (this.agentMode === 'chat') {
        stopYandleChatSession()
      } else {
        stopYandleVoiceCall()
      }
      this.resetAgentUi()
    },
    async onVoiceCallClick() {
      if (
        !this.yandleAgentEnabled ||
        !this.hasTenantId ||
        this.voiceLoading ||
        this.agentSessionActive
      ) {
        return
      }
      stopYandleChatSession({ silent: true })
      this.agentMode = 'voice'
      this.transcriptLines = []
      this.agentSession = null
      this.voiceLoading = true
      try {
        await startYandleVoiceCall({
          tenantId: this.agentTenantId,
          onSessionUpdate: this.onAgentSessionUpdate,
          onTranscriptUpdate: this.onTranscriptUpdate,
        })
      } catch (err) {
        console.error(err)
        this.agentSession = {
          phase: 'error',
          title: 'Could not start call',
          subtitle:
            err instanceof Error
              ? err.message
              : 'Please try again in a moment.',
        }
      } finally {
        this.voiceLoading = false
        if (this.isVoicePanelActive) {
          this.scrollTranscriptToBottom()
        }
      }
    },
    async onChatStartClick() {
      if (
        !this.yandleAgentEnabled ||
        !this.hasTenantId ||
        this.chatLoading ||
        this.agentSessionActive
      ) {
        return
      }
      stopYandleVoiceCall({ silent: true })
      this.agentMode = 'chat'
      this.transcriptLines = []
      this.agentSession = null
      this.chatLoading = true
      try {
        await startYandleChatSession({
          tenantId: this.agentTenantId,
          onSessionUpdate: this.onAgentSessionUpdate,
          onTranscriptUpdate: this.onTranscriptUpdate,
          onTypingUpdate: this.onTypingUpdate,
        })
      } catch (err) {
        console.error(err)
        this.agentSession = {
          phase: 'error',
          title: 'Could not start chat',
          subtitle:
            err instanceof Error
              ? err.message
              : 'Please try again in a moment.',
        }
      } finally {
        this.chatLoading = false
        if (this.isChatPanelActive) {
          this.scrollTranscriptToBottom()
        }
      }
    },
    onChatSend() {
      const text = this.chatDraft.trim()
      if (!text || !this.chatCanSend) return
      if (sendYandleChatMessage(text)) {
        this.chatDraft = ''
        this.transcriptLines = [
          ...this.transcriptLines,
          {
            role: 'user',
            text,
            timestamp: new Date().toISOString(),
            pending: true,
          },
        ]
        this.scrollTranscriptToBottom()
      }
    },
    onTypingUpdate(active) {
      this.agentTyping = active
    },
    transcriptSenderLabel(line) {
      return this.isUserTranscriptRole(line.role) ? 'You' : this.chatAgentName
    },
    transcriptTimeLabel(line) {
      if (line.pending) return 'Sending…'
      const formatted = formatChatTimestamp(line.timestamp)
      return formatted || 'Just now'
    },
    transcriptLineIcon(role) {
      if (!this.isUserTranscriptRole(role)) return 'mdi-robot-outline'
      return this.agentMode === 'voice'
        ? 'mdi-account-voice'
        : 'mdi-account'
    },
    focusChatInput() {
      this.$nextTick(() => {
        const field = this.$refs.chatInput
        const el = field?.$el?.querySelector('input') ?? field?.$el
        el?.focus?.()
      })
    },
    scrollTranscriptToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.transcriptScrollEl
        if (el) {
          el.scrollTop = el.scrollHeight
        }
      })
    },
  },
}
</script>

<style scoped>
.yandle-agent-fab-root {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 2000;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
  pointer-events: none;
}

.yandle-agent-fab-root > * {
  pointer-events: auto;
}

.yandle-agent-fab-root--inline {
  position: relative;
  right: auto;
  bottom: auto;
  align-items: flex-end;
  gap: 8px;
}

.yandle-agent-panel--anchored {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 2001;
}

.yandle-agent-fab {
  border-radius: 50% !important;
  min-width: 56px !important;
  width: 56px !important;
  height: 56px !important;
}

.yandle-agent-fab-root--inline .yandle-agent-fab {
  width: auto !important;
  min-width: 0 !important;
  height: 34px !important;
  padding: 0 12px !important;
  border-radius: 999px !important;
}

.yandle-agent-fab__label {
  font-size: 0.6875rem;
  font-weight: 600;
  color: var(--y-text);
  letter-spacing: 0.01em;
  line-height: 1;
  white-space: nowrap;
  margin-left: 8px;
}

.yandle-agent-panel {
  width: min(380px, calc(100vw - 48px));
  max-height: min(520px, calc(100vh - 120px));
  border-radius: var(--y-radius-xl, 16px);
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(15, 23, 42, 0.96);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  box-shadow:
    0 0 0 1px rgba(255, 255, 255, 0.06) inset,
    0 12px 40px rgba(0, 0, 0, 0.45);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.yandle-agent-panel--chat,
.yandle-agent-panel--voice {
  height: min(520px, calc(100vh - 120px));
}

.yandle-agent-panel__bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 8px;
  padding: 10px 8px 10px 14px;
  border-bottom: 1px solid var(--y-glass-border);
  flex-shrink: 0;
}

.yandle-agent-panel__label {
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--y-text-muted);
}

.yandle-agent-panel__status {
  font-size: 0.75rem;
  color: var(--y-text-muted);
  line-height: 1.3;
  flex: 1 1 100%;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.yandle-agent-panel__body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 10px 12px 12px;
}

.yandle-agent-panel__body--chat,
.yandle-agent-panel__body--voice {
  overflow: hidden;
}

.yandle-agent-panel__content {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.yandle-agent-panel__content--chat,
.yandle-agent-panel__content--voice {
  overflow: hidden;
}

.yandle-agent-modes {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
  flex-shrink: 0;
}

.mode-chip--action {
  cursor: pointer;
}

.mode-chip__spinner {
  margin-inline-end: 6px;
  margin-inline-start: 2px;
}

.chat-thread {
  flex: 1 1 0;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.chat-messages {
  flex: 1 1 0;
  min-height: 0;
  height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 2px 4px 6px;
  scroll-behavior: smooth;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
}

.chat-messages::-webkit-scrollbar {
  width: 5px;
}

.chat-messages::-webkit-scrollbar-thumb {
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.2);
}

.chat-empty-hint {
  flex: 1 1 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 8px;
}

.chat-empty-hint__title {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--y-text);
  margin: 0 0 4px;
}

.chat-empty-hint__subtitle {
  font-size: 0.75rem;
  line-height: 1.4;
  color: var(--y-text-muted);
  margin: 0;
  max-width: 240px;
}

.chat-typing-bubble {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 8px 10px;
  border-radius: 10px;
  border-bottom-left-radius: 3px;
  border: 1px solid var(--y-glass-border);
  background: var(--y-glass-surface);
}

.chat-typing-bubble span {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.45);
  animation: chat-typing-bounce 1.2s infinite ease-in-out;
}

.chat-typing-bubble span:nth-child(2) {
  animation-delay: 0.15s;
}

.chat-typing-bubble span:nth-child(3) {
  animation-delay: 0.3s;
}

@keyframes chat-typing-bounce {
  0%,
  80%,
  100% {
    transform: translateY(0);
    opacity: 0.45;
  }

  40% {
    transform: translateY(-4px);
    opacity: 1;
  }
}

.chat-row {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  margin-bottom: 8px;
}

.chat-row__content {
  display: flex;
  flex-direction: column;
  max-width: calc(100% - 40px);
  align-items: flex-start;
  min-width: 0;
}

.chat-row--user .chat-row__content {
  align-items: flex-end;
}

.chat-row__meta {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 3px;
}

.chat-row--user .chat-row__meta {
  flex-direction: row-reverse;
}

.chat-row__sender {
  font-size: 0.625rem;
  font-weight: 600;
  color: var(--y-text-muted);
}

.chat-row__time {
  font-size: 0.625rem;
  color: var(--y-text-muted);
}

.chat-row--user {
  flex-direction: row-reverse;
}

.chat-row__avatar {
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1px solid var(--y-glass-border);
  background: var(--y-glass-surface);
  color: var(--y-text-muted);
}

.chat-row--user .chat-row__avatar {
  color: #a5b4fc;
  border-color: rgba(99, 102, 241, 0.45);
  background: rgba(99, 102, 241, 0.15);
}

.chat-row__bubble {
  margin: 0;
  padding: 7px 10px;
  border-radius: 10px;
  font-size: 0.75rem;
  line-height: 1.4;
  border: 1px solid var(--y-glass-border);
  background: var(--y-glass-surface);
  color: var(--y-text);
  word-break: break-word;
}

.chat-row--user .chat-row__bubble {
  border-color: rgba(99, 102, 241, 0.4);
  background: rgba(99, 102, 241, 0.18);
  border-bottom-right-radius: 4px;
}

.chat-row--agent .chat-row__bubble {
  border-bottom-left-radius: 4px;
}

.voice-call-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  padding-top: 8px;
  border-top: 1px solid var(--y-glass-border);
}

.voice-call-bar__title {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--y-text);
}

.voice-call-bar__subtitle {
  font-size: 0.6875rem;
  color: var(--y-text-muted);
}

.chat-compose {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  flex-shrink: 0;
  padding-top: 8px;
  border-top: 1px solid var(--y-glass-border);
}

.chat-compose__field {
  flex: 1;
  min-width: 0;
}

.chat-compose__hint {
  font-size: 0.625rem;
  color: var(--y-text-muted);
  margin: 2px 0 0 4px;
}

.yandle-agent-panel-enter-active,
.yandle-agent-panel-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.yandle-agent-panel-enter-from,
.yandle-agent-panel-leave-to {
  opacity: 0;
  transform: translateY(12px) scale(0.96);
}
</style>
